// src/invoices/mail.js — изпращане на фактура/проформа по имейл (Фаза 3.2).
// Легаси мост: window.mailDocOverlay (вика се от модула на проформите).
//
// Промени 16.10.2026 (т. 3б, 3к): прозорецът е голям — вляво самият PDF
// (същият, който получава клиентът), вдясно до кого, тема и текст. До
// няколко получатели („+ Още получател"), всеки се проверява поотделно и на
// сървъра (services/mailRecipients.js). Бутонът „Отказ" е видим във всяка
// тема (по-ярка рамка на .btn.ghost в тъмната — src/shell/theme.js).

import { el, clear, field, input } from '../shared/dom.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { openModal, closeModal } from '../shared/modal.js';
import { dfmt } from '../shared/format.js';
import { state } from '../shared/state.js';

const EMAIL_RE = /^[^@\s,;<>"]+@[^@\s,;<>"]+\.[^@\s,;<>"]+$/;
const MAX_RECIPIENTS = 10;

// Как се казва документът в писмото: известията не са „фактура".
function docWord(kind, doc) {
  if (kind === 'proformas') return { title: 'проформа', text: 'проформа фактура', subj: 'Проформа фактура', the: 'проформата' };
  if (doc && doc.doc_type === 'credit') return { title: 'кредитно известие', text: 'кредитно известие', subj: 'Кредитно известие', the: 'известието' };
  if (doc && doc.doc_type === 'debit') return { title: 'дебитно известие', text: 'дебитно известие', subj: 'Дебитно известие', the: 'известието' };
  return { title: 'фактура', text: 'фактура', subj: 'Фактура', the: 'фактурата' };
}

// opts.onSent() — екранът зад прозореца да научи, че документът вече е
// „Изпратена" (Макс, 02.11.2026: „след „Изпрати" не се отбелязва").
export function mailDocOverlay(kind, doc, contacts, opts) {
  const c = (contacts || []).find((x) => x.id === doc.contact_id) || {};
  const w = docWord(kind, doc);
  const no = doc.number || '';

  // ---- Получателите: по един ред на адрес ----
  const rcpts = [];
  const rcptBox = el('div', { class: 'mail-rcpts' });
  function drawRcpts() {
    clear(rcptBox);
    rcpts.forEach((inp, i) => {
      inp.setAttribute('aria-label', 'Получател ' + (i + 1));
      rcptBox.appendChild(el('div', { class: 'mail-rcpt' }, inp,
        rcpts.length > 1
          ? el('button', { class: 'btn sm danger', type: 'button', text: '×', title: 'Махни този получател', 'aria-label': 'Махни получател ' + (i + 1),
            onClick: () => { rcpts.splice(i, 1); drawRcpts(); } })
          : null));
    });
    addBtn.disabled = rcpts.length >= MAX_RECIPIENTS;
  }
  function addRcpt(value) {
    const inp = input({ type: 'email', value: value || '', placeholder: 'име@фирма.bg', autocomplete: 'email' });
    rcpts.push(inp);
    return inp;
  }
  const addBtn = el('button', { class: 'btn sm ghost', type: 'button', text: '+ Още получател', onClick: () => {
    const inp = addRcpt('');
    drawRcpts();
    inp.focus();
  } });
  // Имейлът на клиента от контакта; ако там има няколко (със запетая) —
  // всеки на свой ред.
  const fromContact = String(c.email || '').split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
  (fromContact.length ? fromContact : ['']).slice(0, MAX_RECIPIENTS).forEach((x) => addRcpt(x));
  drawRcpts();

  const subj = input({ value: w.subj + ' № ' + no });
  // Документът вече НЕ е прикачен (Макс, 17.10.2026): клиентът го изтегля
  // от личния линк под текста — така личи дали го е отворил. Под „Поздрави"
  // — съставилият документа (иначе — който изпраща).
  const signer = doc.created_by_name || (state().user && state().user.name) || '';
  const txt = el('textarea', { style: 'min-height:180px' }, 'Здравейте,\n\nИзпращаме Ви ' + w.text + ' № ' + no + (doc.issue_date ? ' от ' + dfmt(doc.issue_date) : '') + '.\nМожете да изтеглите ' + w.the + ' от линка по-долу.\n\nПоздрави,' + (signer ? '\n' + signer : ''));

  // ---- Прегледът: точно PDF-ът, който клиентът ще изтегли от линка ----
  const pdfUrl = '/api/' + kind + '/' + doc.id + '/pdf';
  // Миниатюрите отнемат мястото на документа; отваряме го по ширината
  // на прегледа, за да се четат редовете още преди изпращането.
  const preview = el('iframe', { class: 'mail-preview', src: pdfUrl + '#navpanes=0&view=FitH', title: 'Преглед: ' + w.title + ' № ' + no });
  const left = el('div', { class: 'mail-left' }, preview);
  const right = el('div', { class: 'mail-right' },
    field('До (имейл)', rcptBox),
    el('div', { style: 'margin:-10px 0 16px' }, addBtn),
    field('Тема', subj),
    field('Съобщение', txt),
    el('p', { class: 'cal-hint', style: 'padding:0', text: 'PDF-ът отляво не се прикачва: под текста на писмото има личен линк, от който клиентът го изтегля. Дали го е отворил, се вижда в „Изпратени".' }));
  const body = el('div', { class: 'mail-grid' }, left, right);

  const sendBtn = el('button', { class: 'btn', type: 'button', text: 'Изпрати' });
  sendBtn.addEventListener('click', async () => {
    const list = rcpts.map((i) => i.value.trim()).filter(Boolean);
    rcpts.forEach((i) => i.classList.remove('bad'));
    if (!list.length) { toast('Въведете имейл на получателя.', true); rcpts[0].classList.add('bad'); rcpts[0].focus(); return; }
    const badInp = rcpts.find((i) => i.value.trim() && !EMAIL_RE.test(i.value.trim()));
    if (badInp) { badInp.classList.add('bad'); badInp.focus(); toast('Невалиден имейл: „' + badInp.value.trim() + '".', true); return; }
    sendBtn.disabled = true; sendBtn.textContent = 'Изпращане…';
    // queued=true значи „наредено в опашката", НЕ „изпратено" (преглед
    // 17.09.2026): при JOBS_MODE=inline писмото може и да се провали после —
    // честното съобщение сочи към екран „Изпратени" за крайния резултат.
    try {
      const r = await api('/' + kind + '/' + doc.id + '/email', { method: 'POST', body: JSON.stringify({ to: list, subject: subj.value, text: txt.value }) });
      const who = list.length === 1 ? list[0] : list.length + ' получатели';
      toast(r && r.queued ? 'Наредено за изпращане до ' + who + ' — резултатът се вижда в „Изпратени".' : 'Изпратено до ' + who + '.');
      closeModal();
      // Сървърът вече е сменил черновата на „Изпратена" (documentMail.js) —
      // формата и списъкът отзад го показват веднага, без презареждане.
      if (kind === 'invoices' && doc.status === 'draft' && !(r && r.queued)) doc.status = 'sent';
      if (opts && typeof opts.onSent === 'function') opts.onSent(doc);
    } catch (e) { toast(e.message, true); sendBtn.disabled = false; sendBtn.textContent = 'Изпрати'; }
  });
  const openBtn = el('button', { class: 'btn sm ghost', type: 'button', text: 'Отвори PDF в нов прозорец', style: 'margin-right:auto', onClick: () => { window.open(pdfUrl, '_blank'); } });
  const foot = el('div', {}, openBtn, el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }), sendBtn);
  const overlay = openModal({ title: 'Изпрати ' + w.title + ' № ' + no + ' по имейл', body, foot, wide: true });
  overlay.classList.add('mail-overlay');
}

window.mailDocOverlay = mailDocOverlay;
